import { useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import { getGoogleBillingSummary } from '../../api/backendApi';
import { formatUtc, formatVendorAmount } from './billingFormat';

function unwrap(response) {
  if (response && response.success === true && response.data) return response.data;
  return response || {};
}

function money(amount, currency) {
  if (amount == null || amount === '') return '—';
  return formatVendorAmount(amount, currency);
}

export default function GoogleBillingSection({ month, linkedTenantId = '', refreshTick = 0 }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getGoogleBillingSummary(month);
        if (cancelled) return;
        setReport(unwrap(response));
      } catch (err) {
        if (cancelled) return;
        setReport(null);
        setError(err?.message || 'Google billing data could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month, refreshTick]);

  const currencyRow = report?.byCurrency?.[0];
  const currency = currencyRow?.currency || '';
  const hasUsage = Boolean(report?.byCurrency?.length);

  return (
    <section className="mt-4" data-testid="google-billing">
      <h2 className="text-lg font-semibold text-gray-900">Google Cloud</h2>
      <p className="mt-2 text-sm font-medium text-amber-800">Google Cloud vendor costs — not customer invoices</p>
      <p className="text-sm text-gray-600">Signal Bridge project cost excludes other projects on the billing account</p>
      <p className="text-sm text-gray-600">Google and Twilio costs are shown separately</p>
      {linkedTenantId ? (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="google-tenant-warning">
          Platform-wide Google cost — not allocated to this tenant.
        </Alert>
      ) : null}
      {report?.monthStatus === 'partial' ? (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="google-partial">
          Current month is partial and may change as Google exports additional usage
        </Alert>
      ) : null}

      {loading ? <p className="mt-4 text-gray-600" data-testid="google-loading">Loading Google billing…</p> : null}
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="google-error">
          {error}
        </Alert>
      ) : null}
      {!loading && !error && report && !hasUsage ? (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="google-empty">
          No Google billing export for {month}. Costs are not shown as zero.
        </Alert>
      ) : null}

      {!loading && !error && hasUsage ? (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="google-summary">
            {[
              ['Signal Bridge project net cost', money(currencyRow.signalBridge?.net, currency)],
              ['Whole billing-account net cost', money(currencyRow.billingAccount?.net, currency)],
              ['Project gross', money(currencyRow.signalBridge?.gross, currency)],
              ['Project credits', money(currencyRow.signalBridge?.credits, currency)],
              ['Currency', currency || '—'],
              ['Export freshness', formatUtc(report.latestExportAt)],
              ['Month status', report.monthStatus === 'partial' ? 'Partial month' : 'Complete month'],
              ['Reconciliation status', report.reconciliationStatus || '—'],
            ].map(([label, value]) => (
              <article key={label} className="rounded border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
              </article>
            ))}
          </div>

          <h3 className="mt-6 text-base font-semibold text-gray-900">Cost types</h3>
          <div className="mt-2 overflow-x-auto rounded border border-gray-200 bg-white">
            <table className="min-w-[640px] w-full text-left text-sm" data-testid="google-cost-types">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Cost type', 'Gross', 'Credits', 'Net'].map((heading) => (
                    <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(currencyRow.costTypes || []).map((row) => (
                  <tr key={row.costType} className="border-t border-gray-100">
                    <td className="px-3 py-2">{row.costType}</td>
                    <td className="px-3 py-2">{money(row.gross, currency)}</td>
                    <td className="px-3 py-2">{money(row.credits, currency)}</td>
                    <td className="px-3 py-2">{money(row.net, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mt-6 text-base font-semibold text-gray-900">Signal Bridge services</h3>
          <div className="mt-2 overflow-x-auto rounded border border-gray-200 bg-white">
            <table className="min-w-[720px] w-full text-left text-sm" data-testid="google-services">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Google service', 'Gross', 'Credits', 'Net', 'Percentage of Signal Bridge net'].map((heading) => (
                    <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(currencyRow.services || []).map((row) => (
                  <tr key={row.service} className="border-t border-gray-100">
                    <td className="px-3 py-2">{row.service}</td>
                    <td className="px-3 py-2">{money(row.gross, currency)}</td>
                    <td className="px-3 py-2">{money(row.credits, currency)}</td>
                    <td className="px-3 py-2">{money(row.net, currency)}</td>
                    <td className="px-3 py-2">{row.percentOfNet == null ? '—' : `${row.percentOfNet}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mt-6 text-base font-semibold text-gray-900">Billing account projects</h3>
          <div className="mt-2 overflow-x-auto rounded border border-gray-200 bg-white">
            <table className="min-w-[720px] w-full text-left text-sm" data-testid="google-projects">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Project', 'Gross', 'Credits', 'Net'].map((heading) => (
                    <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(currencyRow.projects || []).map((row) => (
                  <tr key={row.label} className="border-t border-gray-100" data-testid={`google-project-${row.scope}`}>
                    <td className="px-3 py-2">
                      {row.label}
                      {row.scope === 'outside_signal_bridge' ? (
                        <span className="ml-2 text-xs text-gray-500">Outside Signal Bridge</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{money(row.gross, currency)}</td>
                    <td className="px-3 py-2">{money(row.credits, currency)}</td>
                    <td className="px-3 py-2">{money(row.net, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
